import { EventType } from "@/analytics/analytics-events";
import { ChangeAnnotationCardLayoutEventProperties } from "@/analytics/annotation-events-properties";
import { useTrackElementSizes } from "@/hooks/use-track-element-sizes";
import { grey } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/ielement-types";
import { GetTopicResponse } from "@faro-lotv/service-wires";
import { Skeleton } from "@mui/material";
import { Box } from "@mui/system";
import { debounce } from "es-toolkit";
import { useEffect, useMemo, useState, type JSX } from "react";
import { List, type RowComponentProps } from "react-window";
import { AnnotationListCard } from "./annotation-list-card";
import {
  RESPONSIVE_CARDS_HEIGHT,
  ResponsiveCardVariant,
  cardVariantFromWidth,
} from "./annotation-list-card/responsive-card-variant";
import { AiMarkupData } from "./annotation-panel";

export type AnnotationListProps = {
  /** All annotations to show in the list */
  markupIds?: GUID[];
  /** The data of fetched external markups */
  externalMarkupsData?: Record<GUID, GetTopicResponse>;
  /** The AI data for markups */
  aiMarkupsData?: Record<GUID, AiMarkupData>;
  /** Whether the annotations are still being fetched*/
  markupsLoading?: boolean;
};

type AnnotationItemData = AnnotationListProps & {
  markupIds: GUID[];
  cardVariant: ResponsiveCardVariant;
};

/** @returns a list of annotations cards */
export function AnnotationList({
  markupIds,
  externalMarkupsData,
  aiMarkupsData,
  markupsLoading = false,
}: AnnotationListProps): JSX.Element {
  const [container, setContainer] = useState<HTMLElement>();
  const { width } = useTrackElementSizes(container);

  const data = useMemo(
    () => ({
      markupIds: markupIds ?? [],
      externalMarkupsData,
      aiMarkupsData,
      cardVariant: cardVariantFromWidth(width),
    }),
    [markupIds, externalMarkupsData, aiMarkupsData, width],
  );

  useTrackCardLayoutChange(data.cardVariant, width);

  return (
    <Box
      component="div"
      ref={setContainer}
      sx={{ flexGrow: 1, width: "100%", m: 0, p: 0, overflowY: "auto" }}
    >
      <List<AnnotationItemProps>
        rowComponent={AnnotationItem}
        overscanCount={10}
        // Add one extra item while loading to render a skeleton at the end of the list
        rowCount={data.markupIds.length + (markupsLoading ? 1 : 0)}
        // Adding one pixel to make sure that the cards are getting enough space, to avoid overflow within the list
        rowHeight={RESPONSIVE_CARDS_HEIGHT[data.cardVariant] + 1}
        rowProps={{ data }}
      />
    </Box>
  );
}

/** Properties for a single annotation list item  */
type AnnotationItemProps = {
  /** List data */
  data: AnnotationItemData;
};

/** @returns A single annotation item in the list, managed by react-window*/
function AnnotationItem({
  index,
  data,
  style,
}: RowComponentProps<AnnotationItemProps>): JSX.Element {
  const markupId = data.markupIds.at(index);

  // If the last markup is not available the list is still loading
  if (!markupId) {
    return (
      <Skeleton
        sx={{
          width: "100%",
          height: "32px",
          bgcolor: `${grey[500]}26`,
          ...style,
        }}
      />
    );
  }

  const externalMarkupData = data.externalMarkupsData?.[markupId];
  const aiMarkupData = data.aiMarkupsData?.[markupId];

  return (
    <AnnotationListCard
      key={markupId}
      markupId={markupId}
      externalMarkupData={externalMarkupData}
      aiMarkupData={aiMarkupData}
      sx={{ ...style }}
      variant={data.cardVariant}
    />
  );
}

// The time in ms to wait after the last drag event to assume a drag has been finished by the user
const RELEASE_TIME_AFTER_LAST_DRAG = 2000;

/**
 * Tracks the card variant selected by the user
 *
 * @param cardVariant the current card variant
 * @param width the width of the container
 */
function useTrackCardLayoutChange(
  cardVariant: ResponsiveCardVariant,
  width: number,
): void {
  // the initial card variant should not be tracked as a change
  const [prevCardVariant, setPrevCardVariant] = useState(cardVariant);

  // We are not interested in temporary changes while dragging.
  // Since there is no good access to the drag-end event, here a debounced width change is used as a heuristic.
  const [trackDebounced] = useState(() =>
    debounce(
      (
        cardVariant: ResponsiveCardVariant,
        prevCardVariant: ResponsiveCardVariant,
        // A width change should trigger a debounce signal, but isn't used in the tracking
        // eslint-disable-next-line unused-imports/no-unused-vars
        _lastWidth: number,
      ) => {
        if (cardVariant !== prevCardVariant) {
          Analytics.track<ChangeAnnotationCardLayoutEventProperties>(
            EventType.changeAnnotationCardLayout,
            {
              variant: cardVariant,
            },
          );
          setPrevCardVariant(cardVariant);
        }
      },
      RELEASE_TIME_AFTER_LAST_DRAG,
    ),
  );

  useEffect(() => {
    trackDebounced(cardVariant, prevCardVariant, width);
  }, [cardVariant, prevCardVariant, trackDebounced, width]);
}
